import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle2,
  CopyCheck,
  FileText,
  Info,
  Lightbulb,
  RefreshCw,
  Save,
  Trash2,
  UserCheck,
  UserPlus,
  X,
} from 'lucide-react';
import {
  reconCandidatesKey,
  useBankStatement,
  useDeleteCashPayment,
  useReassignStatementCompany,
  useReconCandidates,
  useReconcileStatement,
  useRefreshMatching,
} from '../hooks/useBankStatements';
import { useCompanies } from '../hooks/useCompanies';
import type {
  InvoiceAllocation,
  PaymentMethod,
  ReconCandidatesDto,
  ReconcileLine,
  ReconStatus,
  StatementTransaction,
} from '../lib/bankStatementsApi';
import { toReconcileLine } from '../lib/bankStatementsApi';
import { useI18n } from '../contexts/I18nContext';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { useCreateClient } from '../hooks/useClients';
import { useServices } from '../hooks/useServices';
import { useCreateSupplier } from '../hooks/useSuppliers';
import { useCreateExpenseCategory } from '../hooks/useExpenseCategories';
import { ClientFormModal } from '../components/clients/ClientFormModal';
import { SupplierFormModal } from '../components/suppliers/SupplierFormModal';
import type { ClientInput } from '../lib/clientsApi';
import type { SupplierInput } from '../lib/suppliersApi';
import { formatCurrency, formatDate } from '../lib/format';
import { FadeIn, SkeletonTable } from '../components/ui/Skeleton';
import { Select as UiSelect } from '../components/ui/Select';
import { InvoiceAllocationsField } from '../components/reconciliation/InvoiceAllocationsField';

type TabKey = 'auto' | 'attention' | 'ignored';

/** Editable per-line state, seeded from the server proposal. */
type LineState = ReconcileLine;

const toLineState = (t: StatementTransaction): LineState => toReconcileLine(t);

function tabOf(s: ReconStatus): TabKey {
  if (s === 'IGNORED') return 'ignored';
  if (s === 'AUTO_MATCHED' || s === 'MANUAL_MATCHED') return 'auto';
  return 'attention';
}

/**
 * Whether a transaction carries a usable historical suggestion that isn't fully
 * applied yet (i.e. some suggested field still differs from the current line).
 */
function hasPendingSuggestion(txn: StatementTransaction, line: LineState): boolean {
  const s = txn.suggestion;
  if (!s) return false;
  return (
    (s.expenseCategoryId != null && s.expenseCategoryId !== line.expenseCategoryId) ||
    (s.matchedSupplierId != null && s.matchedSupplierId !== line.matchedSupplierId) ||
    (s.matchedClientId != null && s.matchedClientId !== line.matchedClientId) ||
    (s.matchedPersonId != null && s.matchedPersonId !== line.matchedPersonId) ||
    (s.matchedProjectId != null && s.matchedProjectId !== line.matchedProjectId) ||
    (s.matchedSubscriptionId != null && s.matchedSubscriptionId !== line.matchedSubscriptionId) ||
    (s.matchedServiceId != null && s.matchedServiceId !== line.matchedServiceId) ||
    (s.reconciliationType != null && s.reconciliationType !== line.reconciliationType)
  );
}

/** Apply a counterparty suggestion onto a line, filling only the fields it has. */
function applySuggestion(txn: StatementTransaction, line: LineState): LineState {
  const s = txn.suggestion;
  if (!s) return line;
  return {
    ...line,
    reconciliationType: s.reconciliationType ?? line.reconciliationType,
    matchedClientId: s.matchedClientId ?? line.matchedClientId,
    matchedSupplierId: s.matchedSupplierId ?? line.matchedSupplierId,
    matchedPersonId: s.matchedPersonId ?? line.matchedPersonId,
    matchedProjectId: s.matchedProjectId ?? line.matchedProjectId,
    matchedSubscriptionId: s.matchedSubscriptionId ?? line.matchedSubscriptionId,
    matchedServiceId: s.matchedServiceId ?? line.matchedServiceId,
    expenseCategoryId: s.expenseCategoryId ?? line.expenseCategoryId,
    // A suggestion is a human-equivalent decision: mark it as manually matched
    // so the line counts as resolved once accepted.
    reconciliationStatus:
      line.reconciliationStatus === 'UNMATCHED' ? 'MANUAL_MATCHED' : line.reconciliationStatus,
  };
}

/**
 * Resolve a human-readable "Auto-matched to: …" label for a reconciled line by
 * looking up the matched entity (client → person → category) against the
 * candidate lists. Returns null when nothing was auto-associated (CLE-150).
 */
function resolveAutoMatchLabel(
  line: ReconcileLine,
  candidates: ReturnType<typeof useReconCandidates>['data'],
): string | null {
  if (!candidates) return null;
  if (line.matchedClientId) {
    const c = candidates.clients.find((x) => x.id === line.matchedClientId);
    if (c) return c.name;
  }
  if (line.matchedSupplierId) {
    const s = candidates.suppliers.find((x) => x.id === line.matchedSupplierId);
    if (s) return s.name;
  }
  if (line.matchedSubscriptionId) {
    const sub = candidates.subscriptions?.find((x) => x.id === line.matchedSubscriptionId);
    if (sub) return sub.name;
  }
  if (line.matchedPersonId) {
    const p = candidates.people.find((x) => x.id === line.matchedPersonId);
    if (p) return p.name;
  }
  if (line.expenseCategoryId) {
    const cat = candidates.categories.find((x) => x.id === line.expenseCategoryId);
    if (cat) return cat.name;
  }
  return null;
}

/**
 * Human-readable summary of a historical suggestion: the entity it matched last
 * time (client → supplier → person → category), for the "Last time: …" hint.
 */
function describeSuggestion(
  s: NonNullable<StatementTransaction['suggestion']>,
  candidates: ReturnType<typeof useReconCandidates>['data'],
): string | null {
  if (!candidates) {
    // Fall back to the raw category code when candidates haven't loaded yet.
    return s.expenseCategoryCode;
  }
  if (s.matchedClientId) {
    const c = candidates.clients.find((x) => x.id === s.matchedClientId);
    if (c) return c.name;
  }
  if (s.matchedSupplierId) {
    const v = candidates.suppliers.find((x) => x.id === s.matchedSupplierId);
    if (v) return v.name;
  }
  if (s.matchedSubscriptionId) {
    const sub = candidates.subscriptions?.find((x) => x.id === s.matchedSubscriptionId);
    if (sub) return sub.name;
  }
  if (s.matchedPersonId) {
    const p = candidates.people.find((x) => x.id === s.matchedPersonId);
    if (p) return p.name;
  }
  if (s.expenseCategoryId) {
    const cat = candidates.categories.find((x) => x.id === s.expenseCategoryId);
    if (cat) return cat.name;
  }
  return s.expenseCategoryCode;
}

/** Quick-add modal target: which kind of entity, for which line, recurring? */
type QuickAdd =
  | { kind: 'client'; lineId: string; prefill: Partial<ClientInput> }
  | { kind: 'supplier'; lineId: string; prefill: Partial<SupplierInput> };

export function BankStatementReconcilePage() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const { id } = useParams<{ id: string }>();
  const { data: statement, isLoading, isError } = useBankStatement(id);
  const { data: candidates } = useReconCandidates(id);
  // Services nomenclature: options for the optional per-line service tag.
  const { data: services } = useServices();
  const serviceOptions = useMemo(
    () => (services ?? []).map((s) => ({ value: s.id, label: s.name })),
    [services],
  );
  const reconcileMutation = useReconcileStatement(id ?? '');
  const refreshMutation = useRefreshMatching(id ?? '');
  const createClientMutation = useCreateClient();
  const createSupplierMutation = useCreateSupplier();
  const createCategoryMutation = useCreateExpenseCategory();
  const queryClient = useQueryClient();
  const deleteCashMutation = useDeleteCashPayment();
  // Multi-company: manual firm reassignment (auto-detect by CUI can be wrong).
  const { data: companies } = useCompanies();
  const multiFirm = (companies?.length ?? 0) > 1;
  const reassignMutation = useReassignStatementCompany();

  const [tab, setTab] = useState<TabKey>('attention');
  const [lines, setLines] = useState<Record<string, LineState>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [quickAdd, setQuickAdd] = useState<QuickAdd | null>(null);
  // Settlement-channel filter chips (local, over the editable line state).
  const [methodFilter, setMethodFilter] = useState<PaymentMethod | 'all'>('all');
  // „din import” filter (spec pas 4): who decided the line last.
  const [sourceFilter, setSourceFilter] = useState<'all' | 'import' | 'manual' | 'auto'>('all');

  // The synthetic cash register (bankCode 'cash'): no auto-matching or
  // finalization — entries are already reconciled; rows can be deleted.
  const isCashRegister = statement?.bankCode === 'cash';

  // Seed editable state once the statement loads.
  useEffect(() => {
    if (!statement) return;
    const seeded: Record<string, LineState> = {};
    for (const t of statement.transactions) seeded[t.id] = toLineState(t);
    setLines(seeded);
  }, [statement]);

  const txnById = useMemo(() => {
    const m = new Map<string, StatementTransaction>();
    statement?.transactions.forEach((t) => m.set(t.id, t));
    return m;
  }, [statement]);

  const grouped = useMemo(() => {
    const g: Record<TabKey, LineState[]> = { auto: [], attention: [], ignored: [] };
    for (const l of Object.values(lines)) g[tabOf(l.reconciliationStatus)].push(l);
    return g;
  }, [lines]);

  // Per-method counts for the settlement-channel filter chips.
  const methodCounts = useMemo(() => {
    const counts: Record<PaymentMethod, number> = { BANK_TRANSFER: 0, CARD: 0, CASH: 0 };
    for (const l of Object.values(lines)) {
      const m = l.paymentMethod ?? 'BANK_TRANSFER';
      counts[m] += 1;
    }
    return counts;
  }, [lines]);

  const total = statement?.transactions.length ?? 0;
  const resolved = total - grouped.attention.length;
  const canFinalize = total > 0 && grouped.attention.length === 0;

  // Duplicate lines surfaced for review (CLE-152). `duplicate` rows were
  // auto-skipped (IGNORED) on import; `possible` rows need a human to decide.
  const duplicateLines = useMemo(
    () => (statement?.transactions ?? []).filter((t) => t.isDuplicate || t.duplicateVerdict),
    [statement],
  );
  const skippedCount = duplicateLines.filter((t) => t.duplicateVerdict === 'duplicate').length;
  const possibleCount = duplicateLines.filter((t) => t.duplicateVerdict === 'possible').length;

  // Lines whose historical suggestion isn't applied yet — drives the "Accept all
  // suggestions" button and per-line indicators (CLE-153).
  const pendingSuggestions = useMemo(() => {
    let n = 0;
    for (const l of Object.values(lines)) {
      const t = txnById.get(l.id);
      if (t && hasPendingSuggestion(t, l)) n += 1;
    }
    return n;
  }, [lines, txnById]);

  function acceptSuggestion(lineId: string) {
    const txn = txnById.get(lineId);
    if (!txn) return;
    setLines((prev) => ({ ...prev, [lineId]: applySuggestion(txn, prev[lineId]) }));
  }

  function acceptAllSuggestions() {
    setLines((prev) => {
      const next = { ...prev };
      for (const [lineId, line] of Object.entries(prev)) {
        const txn = txnById.get(lineId);
        if (txn && hasPendingSuggestion(txn, line)) next[lineId] = applySuggestion(txn, line);
      }
      return next;
    });
    setMessage({ kind: 'ok', text: 'Au fost aplicate toate sugestiile istorice.' });
  }

  function patch(lineId: string, p: Partial<LineState>) {
    setLines((prev) => ({ ...prev, [lineId]: { ...prev[lineId], ...p } }));
  }

  function markIgnored(lineId: string) {
    // Ignore lives in the status only (CLE-164); the type stays the strict
    // INCOME/OUTCOME direction so the row keeps its income/expense disposition.
    patch(lineId, { reconciliationStatus: 'IGNORED' });
  }

  function confirmLine(lineId: string) {
    patch(lineId, { reconciliationStatus: 'MANUAL_MATCHED' });
  }

  function restore(lineId: string) {
    patch(lineId, { reconciliationStatus: 'UNMATCHED' });
  }

  async function save(finalize: boolean) {
    if (!id) return;
    setMessage(null);
    try {
      const result = await reconcileMutation.mutateAsync({
        lines: Object.values(lines),
        finalize,
      });
      setMessage({
        kind: 'ok',
        text: finalize
          ? `Reconcilierea a fost finalizată. ${result.recorded} înregistrări scrise în Financiar.`
          : 'Ciornă salvată.',
      });
    } catch (err) {
      setMessage({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Salvarea a eșuat.',
      });
    }
  }

  async function handleRefresh() {
    if (!id) return;
    setMessage(null);
    try {
      const result = await refreshMutation.mutateAsync();
      // Re-seed editable state from the freshly matched server rows.
      setLines((prev) => {
        const seeded = { ...prev };
        for (const t of result.transactions) seeded[t.id] = toLineState(t);
        return seeded;
      });
      setMessage({
        kind: 'ok',
        text:
          result.newMatches > 0
            ? `${result.newMatches} ${result.newMatches === 1 ? 'potrivire nouă găsită' : 'potriviri noi găsite'}.`
            : 'Nicio potrivire nouă găsită.',
      });
    } catch (err) {
      setMessage({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Reîmprospătarea a eșuat.',
      });
    }
  }

  // Reassign the statement to another firm. NULL companyId = default firm on
  // the backend, so selecting the default firm sends null.
  async function handleReassignCompany(value: string) {
    if (!id) return;
    setMessage(null);
    const defaultId = companies?.find((c) => c.isDefault)?.id;
    try {
      await reassignMutation.mutateAsync({ id, companyId: value === defaultId ? null : value });
      setMessage({ kind: 'ok', text: t('bank.company.reassigned') });
    } catch (err) {
      setMessage({
        kind: 'err',
        text: err instanceof Error ? err.message : t('bank.company.reassignFailed'),
      });
    }
  }

  // Open the inline quick-add modal for a line, pre-filled from the counterparty.
  function openQuickAddClient(txn: StatementTransaction) {
    setQuickAdd({
      kind: 'client',
      lineId: txn.id,
      prefill: {
        name: txn.counterparty ?? '',
        notes: txn.counterpartyIban ? `IBAN: ${txn.counterpartyIban}` : undefined,
      },
    });
  }

  function openQuickAddSupplier(txn: StatementTransaction, recurring: boolean) {
    setQuickAdd({
      kind: 'supplier',
      lineId: txn.id,
      prefill: {
        name: txn.counterparty ?? '',
        iban: txn.counterpartyIban ?? undefined,
        isRecurring: recurring,
      },
    });
  }

  // Put a just-created client/supplier/category into this page's dropdowns
  // right away, then refetch the candidates for the server's view.
  function addCandidate(update: (c: ReconCandidatesDto) => ReconCandidatesDto) {
    queryClient.setQueryData<ReconCandidatesDto>(reconCandidatesKey(id), (c) =>
      c ? update(c) : c,
    );
    void queryClient.invalidateQueries({ queryKey: reconCandidatesKey(id) });
  }

  async function handleCreateCategory(name: string, kind: 'INCOME' | 'EXPENSE') {
    const created = await createCategoryMutation.mutateAsync({
      name,
      kind,
      companyId: statement?.companyId ?? null,
    });
    addCandidate((c) => ({
      ...c,
      categories: [...c.categories, { id: created.id, code: created.code, name: created.name }],
    }));
    return created.id;
  }

  async function handleQuickAddClient(input: ClientInput) {
    if (!quickAdd) return;
    const created = await createClientMutation.mutateAsync(input);
    addCandidate((c) => ({
      ...c,
      clients: [...c.clients, { id: created.id, name: created.name }],
    }));
    patch(quickAdd.lineId, {
      matchedClientId: created.id,
      reconciliationType: 'INCOME',
      reconciliationStatus: 'MANUAL_MATCHED',
    });
    setQuickAdd(null);
    setMessage({ kind: 'ok', text: `Clientul „${created.name}” a fost adăugat și asociat.` });
  }

  async function handleQuickAddSupplier(input: SupplierInput) {
    if (!quickAdd) return;
    const created = await createSupplierMutation.mutateAsync(input);
    addCandidate((c) => ({
      ...c,
      suppliers: [
        ...c.suppliers,
        {
          id: created.id,
          name: created.name,
          isRecurring: created.isRecurring,
          expenseCategoryId: created.expenseCategoryId ?? null,
        },
      ],
    }));
    patch(quickAdd.lineId, {
      matchedSupplierId: created.id,
      reconciliationType: 'OUTCOME',
      reconciliationStatus: 'MANUAL_MATCHED',
    });
    setQuickAdd(null);
    setMessage({ kind: 'ok', text: `Furnizorul „${created.name}” a fost adăugat și asociat.` });
  }

  // Cash-register only: remove an entry (bank line + its ledger row).
  async function handleDeleteCash(lineId: string) {
    if (!(await confirm({ message: t('cash.deleteConfirm'), danger: true }))) return;
    setMessage(null);
    try {
      await deleteCashMutation.mutateAsync(lineId);
      setLines((prev) => {
        const next = { ...prev };
        delete next[lineId];
        return next;
      });
      setMessage({ kind: 'ok', text: t('cash.deleted') });
    } catch (err) {
      setMessage({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Ștergerea a eșuat.',
      });
    }
  }

  if (isLoading) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <SkeletonTable rows={8} columns={5} />
      </div>
    );
  }
  if (isError || !statement) {
    return (
      <div className="w-full p-8">
        <p className="text-red-600">Nu s-a putut încărca extrasul.</p>
        <Link to="/financials/bank-statements" className="text-brand-700 hover:underline">
          ← Înapoi la extrase
        </Link>
      </div>
    );
  }

  const TABS: Array<{ key: TabKey; label: string; count: number; dot: string }> = [
    { key: 'auto', label: 'Potrivite automat', count: grouped.auto.length, dot: 'bg-emerald-500' },
    {
      key: 'attention',
      label: 'Necesită verificare',
      count: grouped.attention.length,
      dot: 'bg-amber-500',
    },
    { key: 'ignored', label: 'Ignorate', count: grouped.ignored.length, dot: 'bg-gray-400' },
  ];

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <Link
        to="/financials/bank-statements"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft size={14} /> Extrase bancare
      </Link>

      {/* Header */}
      <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{statement.bankName}</h1>
          <p className="text-sm text-gray-500">
            {statement.iban ?? '—'}
            {statement.bankAccount && (
              <span className="ml-1 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                {statement.bankAccount.label ?? statement.bankAccount.bankName}
              </span>
            )}{' '}
            · {formatDate(statement.periodStart)} – {formatDate(statement.periodEnd)} · {total}{' '}
            tranzacții
          </p>
          {multiFirm && companies && !isCashRegister && (
            <div className="mt-2 max-w-xs">
              <UiSelect
                label={t('company.switcher.label')}
                hideLabel
                value={statement.companyId ?? companies.find((c) => c.isDefault)?.id ?? ''}
                onChange={(v) => void handleReassignCompany(v)}
                options={companies.map((c) => ({
                  value: c.id,
                  label: c.displayName || c.legalName,
                }))}
                size="sm"
                disabled={statement.status === 'RECONCILED' || reassignMutation.isPending}
              />
              {statement.status === 'RECONCILED' && (
                <p className="mt-1 text-xs text-gray-400">{t('bank.company.finalizedLocked')}</p>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {statement.pdfUrl && !isCashRegister && (
            <button
              type="button"
              onClick={() => window.open(statement.pdfUrl!, '_blank', 'noopener')}
              title="Deschide PDF-ul original al extrasului într-un tab nou."
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <FileText size={16} /> Vezi PDF
            </button>
          )}
          {pendingSuggestions > 0 && (
            <button
              type="button"
              onClick={acceptAllSuggestions}
              title="Precompletează fiecare linie pe baza modului în care contrapartea a fost categorisită ultima dată."
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
            >
              <Lightbulb size={16} /> Acceptă toate sugestiile ({pendingSuggestions})
            </button>
          )}
          {!isCashRegister && (
            <button
              type="button"
              onClick={() => void handleRefresh()}
              disabled={refreshMutation.isPending}
              title="Rulează din nou potrivirea automată cu clienții și furnizorii actuali."
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={16} className={refreshMutation.isPending ? 'animate-spin' : ''} />{' '}
              Reîmprospătează potrivirea
            </button>
          )}
          <button
            type="button"
            onClick={() => void save(false)}
            disabled={reconcileMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Save size={16} /> Salvează ciorna
          </button>
          {!isCashRegister && (
            <button
              type="button"
              onClick={() => void save(true)}
              disabled={reconcileMutation.isPending || !canFinalize}
              title={canFinalize ? '' : 'Reconciliază sau ignoră mai întâi toate tranzacțiile.'}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              <CheckCircle2 size={16} /> Finalizează reconcilierea
            </button>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="mt-4">
        <div className="mb-1 flex justify-between text-xs text-gray-500">
          <span>
            {resolved} / {total} rezolvate
          </span>
          <span>{total > 0 ? Math.round((resolved / total) * 100) : 0}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all"
            style={{ width: `${total > 0 ? (resolved / total) * 100 : 0}%` }}
          />
        </div>
      </div>

      {duplicateLines.length > 0 && (
        <DuplicateBanner
          lines={duplicateLines}
          skippedCount={skippedCount}
          possibleCount={possibleCount}
        />
      )}

      {message && (
        <div
          className={`mt-4 rounded-md border px-3 py-2 text-sm ${
            message.kind === 'ok'
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
              : 'border-red-500/30 bg-red-500/10 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Settlement-channel filter chips (Toate / Transfer / Card / Cash). */}
      <div className="mt-6 flex flex-wrap gap-1.5">
        {(['all', 'BANK_TRANSFER', 'CARD', 'CASH'] as const).map((m) => {
          const count =
            m === 'all'
              ? methodCounts.BANK_TRANSFER + methodCounts.CARD + methodCounts.CASH
              : methodCounts[m];
          return (
            <button
              key={m}
              type="button"
              onClick={() => setMethodFilter(m)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
                methodFilter === m
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {m === 'all' ? t('paymentMethod.all') : t(`paymentMethod.${m}`)}
              <span
                className={`rounded-full px-1.5 text-[10px] ${
                  methodFilter === m ? 'bg-gray-300' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Provenance filter: lines written by the reconciliation import, by a
          person, or by the matcher. */}
      {(sourceFilter !== 'all' ||
        (statement?.transactions ?? []).some((t) => t.reconciliationSource === 'import')) && (
        <div className="mt-3 flex flex-wrap items-center gap-1 text-xs">
          <span className="mr-1 text-gray-500">Proveniență:</span>
          {(
            [
              ['all', 'Toate'],
              ['import', 'Din import'],
              ['manual', 'Decise de mână'],
              ['auto', 'Automat'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setSourceFilter(key)}
              className={`rounded-full border px-2 py-0.5 ${
                sourceFilter === key
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="mt-3 flex gap-1 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.key
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${t.dot}`} />
            {t.label}
            <span className="rounded-full bg-gray-100 px-1.5 text-xs text-gray-600">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="mt-4">
        <FadeIn>
          {(() => {
            const visible = grouped[tab]
              .filter(
                (l) =>
                  methodFilter === 'all' || (l.paymentMethod ?? 'BANK_TRANSFER') === methodFilter,
              )
              .filter(
                (l) =>
                  sourceFilter === 'all' ||
                  txnById.get(l.id)?.reconciliationSource === sourceFilter,
              );
            if (visible.length === 0) {
              return <p className="py-10 text-center text-gray-500">Nicio tranzacție aici.</p>;
            }
            return (
              <div className="space-y-2">
                {visible.map((line) => {
                  const txn = txnById.get(line.id);
                  if (!txn) return null;
                  return (
                    <ReconcileRow
                      key={line.id}
                      txn={txn}
                      line={line}
                      tab={tab}
                      candidates={candidates}
                      serviceOptions={serviceOptions}
                      isCashRegister={isCashRegister}
                      onPatch={(p) => patch(line.id, p)}
                      onConfirm={() => confirmLine(line.id)}
                      onIgnore={() => markIgnored(line.id)}
                      onRestore={() => restore(line.id)}
                      onAddClient={() => openQuickAddClient(txn)}
                      onAddSupplier={(recurring) => openQuickAddSupplier(txn, recurring)}
                      onCreateCategory={(name) =>
                        handleCreateCategory(name, txn.type === 'credit' ? 'INCOME' : 'EXPENSE')
                      }
                      onAcceptSuggestion={() => acceptSuggestion(line.id)}
                      onDelete={() => void handleDeleteCash(line.id)}
                    />
                  );
                })}
              </div>
            );
          })()}
        </FadeIn>
      </div>

      {/* Totals footer: gross credit/debit tie-out against the PDF header +
          what will actually be recorded in Financials, live over the editable
          line statuses. */}
      <TotalsFooter
        transactions={statement.transactions}
        lines={lines}
        headerCredits={statement.totalCredits}
        headerDebits={statement.totalDebits}
        finalized={statement.status === 'RECONCILED'}
      />

      {/* Inline quick-add modal (CLE-151) — pre-filled from the counterparty. */}
      {quickAdd?.kind === 'client' && (
        <ClientFormModal
          title="Adaugă client"
          prefill={quickAdd.prefill}
          submitting={createClientMutation.isPending}
          onClose={() => setQuickAdd(null)}
          onSubmit={handleQuickAddClient}
        />
      )}
      {quickAdd?.kind === 'supplier' && (
        <SupplierFormModal
          title={quickAdd.prefill.isRecurring ? 'Adaugă furnizor recurent' : 'Adaugă furnizor'}
          prefill={quickAdd.prefill}
          submitting={createSupplierMutation.isPending}
          onClose={() => setQuickAdd(null)}
          onSubmit={handleQuickAddSupplier}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Totals footer: 1:1 tie-out between the statement lines and the PDF header
// totals, plus a live "what gets recorded in Financials" net view. Amounts come
// from the immutable server transactions; statuses from the editable lines so
// ignore/restore updates the numbers instantly.
// ---------------------------------------------------------------------------

interface TotalsFooterProps {
  transactions: StatementTransaction[];
  lines: Record<string, LineState>;
  headerCredits: number | null;
  headerDebits: number | null;
  finalized: boolean;
}

function TotalsFooter({
  transactions,
  lines,
  headerCredits,
  headerDebits,
  finalized,
}: TotalsFooterProps) {
  const totals = useMemo(() => {
    const acc = {
      credits: 0,
      debits: 0,
      recCredits: 0,
      recDebits: 0,
      internalIgnored: 0,
      manualIgnored: 0,
    };
    for (const txn of transactions) {
      if (txn.isDuplicate) continue;
      const isCredit = txn.type === 'credit';
      if (isCredit) acc.credits += txn.amount;
      else acc.debits += txn.amount;
      const status = lines[txn.id]?.reconciliationStatus ?? txn.reconciliationStatus;
      if (status === 'AUTO_MATCHED' || status === 'MANUAL_MATCHED') {
        if (isCredit) acc.recCredits += txn.amount;
        else acc.recDebits += txn.amount;
      } else if (status === 'IGNORED') {
        if (txn.isInternalTransfer) acc.internalIgnored += txn.amount;
        else acc.manualIgnored += txn.amount;
      }
    }
    return acc;
  }, [transactions, lines]);

  const currency = transactions[0]?.currency ?? 'RON';

  // ✓ when the summed lines match the PDF header total within a cent;
  // no badge at all when the header carried no totals (cash register).
  const tieOut = (headerValue: number | null, computed: number) => {
    if (headerValue == null) return null;
    const delta = computed - headerValue;
    if (Math.abs(delta) <= 0.01) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
          ✓ Corespunde extrasului
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-800">
        Diferență față de extras: {delta > 0 ? '+' : '−'}
        {formatCurrency(Math.abs(delta), currency)}
      </span>
    );
  };

  if (transactions.length === 0) return null;

  return (
    <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-col gap-2">
        {/* Row 1: gross totals + tie-out vs the PDF header. */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          <span className="text-gray-600">
            Total creditat:{' '}
            <span className="font-semibold text-emerald-600">
              +{formatCurrency(totals.credits, currency)}
            </span>
          </span>
          {tieOut(headerCredits, totals.credits)}
          <span className="text-gray-600">
            Total debitat:{' '}
            <span className="font-semibold text-red-600">
              −{formatCurrency(totals.debits, currency)}
            </span>
          </span>
          {tieOut(headerDebits, totals.debits)}
        </div>

        {/* Row 2: what actually reaches the Financials records. */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          <span className="font-medium text-gray-800">
            {finalized ? 'Înregistrat în Financiar:' : 'Se va înregistra în Financiar:'}
          </span>
          <span className="text-gray-600">
            venituri{' '}
            <span className="font-semibold text-emerald-600">
              +{formatCurrency(totals.recCredits, currency)}
            </span>
          </span>
          <span className="text-gray-600">
            cheltuieli{' '}
            <span className="font-semibold text-red-600">
              −{formatCurrency(totals.recDebits, currency)}
            </span>
          </span>
        </div>

        {/* Row 3: what stays out, only when non-zero. */}
        {(totals.internalIgnored > 0 || totals.manualIgnored > 0) && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-500">
            {totals.internalIgnored > 0 && (
              <span>
                Transferuri interne (ignorate): {formatCurrency(totals.internalIgnored, currency)}
              </span>
            )}
            {totals.manualIgnored > 0 && (
              <span>Ignorate manual: {formatCurrency(totals.manualIgnored, currency)}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface RowProps {
  txn: StatementTransaction;
  line: ReconcileLine;
  tab: TabKey;
  candidates: ReturnType<typeof useReconCandidates>['data'];
  serviceOptions: Array<{ value: string; label: string }>;
  isCashRegister: boolean;
  onPatch: (p: Partial<ReconcileLine>) => void;
  onConfirm: () => void;
  onIgnore: () => void;
  onRestore: () => void;
  onAddClient: () => void;
  onAddSupplier: (recurring: boolean) => void;
  onCreateCategory: (name: string) => Promise<string>;
  onAcceptSuggestion: () => void;
  onDelete: () => void;
}

function ReconcileRow({
  txn,
  line,
  tab,
  candidates,
  serviceOptions,
  isCashRegister,
  onPatch,
  onConfirm,
  onIgnore,
  onRestore,
  onAddClient,
  onAddSupplier,
  onCreateCategory,
  onAcceptSuggestion,
  onDelete,
}: RowProps) {
  const { t } = useI18n();
  const isCredit = txn.type === 'credit';
  const amountCls = isCredit ? 'text-emerald-500' : 'text-red-500';
  const sign = isCredit ? '+' : '−';
  // Income/expense disposition (CLE-150/CLE-156): credit → Income, debit →
  // Expense. This IS the transaction type — auto-set and read-only, shown as a
  // badge. There is no "Other"; the matcher's semantic recon-type is internal.
  const flow = isCredit ? 'Venit' : 'Cheltuială';
  const flowCls = isCredit ? 'bg-emerald-500/15 text-emerald-600' : 'bg-red-500/15 text-red-600';

  // Settlement channel: badge only when it differs from the default transfer.
  const method = line.paymentMethod ?? txn.paymentMethod;
  const methodBadgeCls =
    method === 'CARD' ? 'bg-sky-500/15 text-sky-700' : 'bg-amber-500/15 text-amber-700';

  // Clean title (CLE-156): the cleaned counterparty/merchant name or a short
  // summary. Never the raw PDF description — that lives in the collapsible
  // "Description" section below.
  const title = txn.title ?? txn.counterparty ?? 'Tranzacție';
  const hasRawDescription = !!txn.description && txn.description.trim() !== title.trim();
  const [showDescription, setShowDescription] = useState(false);
  // Raw-data popover (CLE-165): all untransformed fields for this line.
  const [showRaw, setShowRaw] = useState(false);

  // "Auto-matched to: …" — resolve the matched entity's display name so the
  // user sees what the matcher picked, not just an opaque id (CLE-150).
  const autoMatchedTo = resolveAutoMatchLabel(line, candidates);

  // Historical suggestion (CLE-153): show a subtle hint when this counterparty
  // was categorised before, and a one-click accept while it's still pending.
  const suggestion = txn.suggestion;
  const suggestionPending = hasPendingSuggestion(txn, line);
  const suggestionLabel = suggestion ? describeSuggestion(suggestion, candidates) : null;

  // Administrator payment/loan (CLE-ADMIN): informational badge + manual
  // quick-picks between the administrator categories, filtered by direction —
  // DEBIT: salary / dividend / loan repayment; CREDIT: loan into the company.
  // The buttons are hidden when the seeded category codes are missing from the DB.
  const adminMatch = txn.administratorMatch;
  const adminQuickPickCodes = isCredit
    ? ['imprumut_administrator']
    : ['salariu_administrator', 'dividende', 'restituire_imprumut_administrator'];
  const adminCategories = (candidates?.categories ?? []).filter((c) =>
    adminQuickPickCodes.includes(c.code),
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{title}</span>
            <button
              type="button"
              onClick={() => setShowRaw(true)}
              title="Arată toate datele brute ale tranzacției din extras"
              aria-label="Arată datele brute ale tranzacției"
              className="inline-flex items-center justify-center rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <Info size={14} />
            </button>
            <span
              title="Setat automat în funcție de direcția tranzacției — creditul este venit, debitul este cheltuială."
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${flowCls}`}
            >
              {flow}
            </span>
            {method !== 'BANK_TRANSFER' && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${methodBadgeCls}`}
              >
                {t(`paymentMethod.${method}`)}
              </span>
            )}
            {txn.reconciliationConfidence && tab === 'auto' && (
              <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-400">
                {txn.reconciliationConfidence}
              </span>
            )}
            {txn.duplicateVerdict === 'duplicate' && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                <CopyCheck size={11} /> Duplicat
              </span>
            )}
            {txn.duplicateVerdict === 'possible' && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                <CopyCheck size={11} /> Posibil duplicat
              </span>
            )}
            {txn.isInternalTransfer && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                ⇄ Transfer intern
              </span>
            )}
          </div>
          {/* Full raw PDF text kept for reference in a collapsible section (CLE-156). */}
          {hasRawDescription && (
            <div className="mt-0.5">
              <button
                type="button"
                onClick={() => setShowDescription((v) => !v)}
                className="text-[11px] font-medium text-gray-400 hover:text-gray-600"
              >
                {showDescription ? 'Ascunde descrierea' : 'Arată descrierea'}
              </button>
              {showDescription && (
                <div className="mt-1 whitespace-pre-wrap break-words rounded bg-gray-50 px-2 py-1 text-[11px] text-gray-500">
                  {txn.description}
                </div>
              )}
            </div>
          )}
          {txn.reconciliationSource === 'import' && (
            <div className="mt-1 mr-1 inline-flex items-center gap-1 rounded bg-sky-500/10 px-1.5 py-0.5 text-[11px] font-medium text-sky-700">
              Din import · {txn.matchReason ?? 'reconciliere importată'}
            </div>
          )}
          {txn.reconciliationSource === 'manual' && txn.importBatchId && (
            <div className="mt-1 mr-1 inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
              Din import, modificată de mână
            </div>
          )}
          {tab === 'auto' && autoMatchedTo && (
            <div className="mt-1 inline-flex items-center gap-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
              Potrivit automat cu: {autoMatchedTo}
            </div>
          )}
          {tab !== 'ignored' && adminMatch && (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded bg-violet-500/10 px-1.5 py-0.5 text-[11px] font-medium text-violet-700">
                <UserCheck size={12} />
                {t(isCredit ? 'companies.admins.loanBadge' : 'companies.admins.paymentBadge', {
                  name: adminMatch.fullName,
                  role: t(`companies.admins.role.${adminMatch.role}`),
                })}
              </span>
              {!txn.isInternalTransfer &&
                adminCategories.map((cat) => {
                  const active = line.expenseCategoryId === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() =>
                        onPatch({
                          expenseCategoryId: cat.id,
                          matchedPersonId: adminMatch.personId,
                          // A loan from the administrator is never a client
                          // payment — clear any fuzzy client match.
                          ...(isCredit ? { matchedClientId: null } : {}),
                        })
                      }
                      className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${
                        active
                          ? 'border-violet-500 bg-violet-500/15 text-violet-800'
                          : 'border-violet-200 bg-white text-violet-700 hover:bg-violet-50'
                      }`}
                    >
                      {cat.name}
                    </button>
                  );
                })}
            </div>
          )}
          {tab !== 'ignored' && suggestion && suggestionLabel && (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                <Lightbulb size={12} /> Ultima dată: {suggestionLabel}
                {suggestion.occurrenceCount > 1 ? ` · văzut de ${suggestion.occurrenceCount}×` : ''}
              </span>
              {suggestion.alsoSeenCategories.length > 0 && (
                <span className="text-[11px] text-gray-400">
                  Văzut și ca: {suggestion.alsoSeenCategories.join(', ')}
                </span>
              )}
              {suggestionPending && (
                <button
                  type="button"
                  onClick={onAcceptSuggestion}
                  className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
                >
                  Acceptă
                </button>
              )}
            </div>
          )}
          <div className="mt-0.5 text-xs text-gray-400">
            {formatDate(txn.date)}
            {txn.matchReason ? ` · ${txn.matchReason}` : ''}
            {txn.originalAmount
              ? ` · ${formatCurrency(txn.originalAmount, txn.originalCurrency ?? 'EUR')}`
              : ''}
          </div>
        </div>
        <div className={`shrink-0 text-right text-sm font-semibold ${amountCls}`}>
          {sign}
          {formatCurrency(txn.amount, txn.currency)}
        </div>
      </div>

      {/* Manual editing controls (needs-attention + auto for re-edit). The Type
          is auto-set (Income/Expense badge above), so it is not editable here —
          only the supplier/person/category/project associations are (CLE-156).
          Internal-transfer lines don't get dropdowns — they are auto-ignored. */}
      {tab !== 'ignored' && !txn.isInternalTransfer && (
        <div className="mt-3 grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
          {isCredit ? (
            <>
              <Select
                label="Client"
                value={line.matchedClientId ?? ''}
                onChange={(v) => onPatch({ matchedClientId: v || null })}
                options={(candidates?.clients ?? []).map((c) => ({ value: c.id, label: c.name }))}
              />

              {/* Personal inflows (administrator loans, CLE-ADMIN): a credit can
                  come from a person, with its own income category. */}
              <Select
                label="Persoană"
                value={line.matchedPersonId ?? ''}
                onChange={(v) => onPatch({ matchedPersonId: v || null })}
                options={(candidates?.people ?? []).map((p) => ({ value: p.id, label: p.name }))}
              />
              <Select
                label="Categorie"
                value={line.expenseCategoryId ?? ''}
                onChange={(v) => onPatch({ expenseCategoryId: v || null })}
                onCreate={onCreateCategory}
                // Only exclude the "expense" placeholder — "income" is the
                // baseline default every credit line starts with.
                options={(candidates?.categories ?? [])
                  .filter((c) => c.code !== 'expense')
                  .map((c) => ({ value: c.id, label: c.name }))}
              />
            </>
          ) : (
            <>
              <Select
                label="Furnizor"
                value={line.matchedSupplierId ?? ''}
                onChange={(v) => {
                  const patch: Partial<ReconcileLine> = { matchedSupplierId: v || null };
                  // Auto-fill the subscription when the chosen supplier has
                  // exactly one linked subscription (the user can clear it).
                  const subscriptionCandidates = candidates?.subscriptions ?? [];
                  let linkedSub: (typeof subscriptionCandidates)[number] | undefined;
                  if (v && !line.matchedSubscriptionId) {
                    const linked = subscriptionCandidates.filter((s) => s.supplierId === v);
                    if (linked.length === 1) {
                      linkedSub = linked[0];
                      patch.matchedSubscriptionId = linkedSub.id;
                    }
                  }
                  // Auto-fill the default expense category (subscription's wins
                  // over the supplier's) — only when the line has none yet.
                  if (v && !line.expenseCategoryId) {
                    const supplier = (candidates?.suppliers ?? []).find((s) => s.id === v);
                    const defaultCategory =
                      linkedSub?.expenseCategoryId ?? supplier?.expenseCategoryId ?? null;
                    if (defaultCategory) patch.expenseCategoryId = defaultCategory;
                  }
                  onPatch(patch);
                }}
                options={(candidates?.suppliers ?? []).map((s) => ({
                  value: s.id,
                  label: s.isRecurring ? `${s.name} · recurent` : s.name,
                }))}
              />
              <Select
                label={t('recon.subscription')}
                value={line.matchedSubscriptionId ?? ''}
                onChange={(v) => {
                  const patch: Partial<ReconcileLine> = { matchedSubscriptionId: v || null };
                  // Auto-fill the subscription's default expense category (or
                  // the linked supplier's) — only when the line has none yet.
                  if (v && !line.expenseCategoryId) {
                    const sub = (candidates?.subscriptions ?? []).find((s) => s.id === v);
                    const linkedSupplier = sub?.supplierId
                      ? (candidates?.suppliers ?? []).find((s) => s.id === sub.supplierId)
                      : undefined;
                    const defaultCategory =
                      sub?.expenseCategoryId ?? linkedSupplier?.expenseCategoryId ?? null;
                    if (defaultCategory) patch.expenseCategoryId = defaultCategory;
                  }
                  onPatch(patch);
                }}
                // Subscriptions linked to the matched supplier sort first.
                options={[...(candidates?.subscriptions ?? [])]
                  .sort((a, b) => {
                    if (line.matchedSupplierId) {
                      const aLinked = a.supplierId === line.matchedSupplierId ? 0 : 1;
                      const bLinked = b.supplierId === line.matchedSupplierId ? 0 : 1;
                      if (aLinked !== bLinked) return aLinked - bLinked;
                    }
                    return a.name.localeCompare(b.name, 'ro');
                  })
                  .map((s) => ({
                    value: s.id,
                    label: s.name,
                  }))}
              />
              <Select
                label="Persoană"
                value={line.matchedPersonId ?? ''}
                onChange={(v) => onPatch({ matchedPersonId: v || null })}
                options={(candidates?.people ?? []).map((p) => ({ value: p.id, label: p.name }))}
              />
              <Select
                label="Categorie cheltuială"
                value={line.expenseCategoryId ?? ''}
                onChange={(v) => onPatch({ expenseCategoryId: v || null })}
                onCreate={onCreateCategory}
                // Show real spend categories only — the "income"/"expense"
                // placeholders are the income/expense TYPE, not a category, and
                // listing them here is circular (CLE-156).
                options={(candidates?.categories ?? [])
                  .filter((c) => c.code !== 'income' && c.code !== 'expense')
                  .map((c) => ({ value: c.id, label: c.name }))}
              />
            </>
          )}
          {/* Cash-register entries are never finalized, so a payment there would
              never count: invoices are paid from bank statements only. */}
          {!isCashRegister && (
            <InvoiceAllocationsField
              direction={isCredit ? 'credit' : 'debit'}
              lineAmount={txn.amount}
              lineCurrency={txn.currency}
              allocations={(line.allocations as InvoiceAllocation[] | undefined) ?? []}
              invoices={candidates?.invoices ?? []}
              onChange={(next) =>
                onPatch({
                  allocations: next,
                  matchedInvoiceId: next.find((a) => a.invoiceId)?.invoiceId ?? null,
                })
              }
            />
          )}
          <Select
            label="Proiect"
            value={line.matchedProjectId ?? ''}
            onChange={(v) => onPatch({ matchedProjectId: v || null })}
            options={(candidates?.projects ?? []).map((p) => ({
              value: p.id,
              label: p.code ? `${p.code} · ${p.name}` : p.name,
            }))}
          />
          <Select
            label="Serviciu (opțional)"
            value={line.matchedServiceId ?? ''}
            onChange={(v) => onPatch({ matchedServiceId: v || null })}
            options={serviceOptions}
          />
          <div className="block">
            <span className="mb-1 block text-[11px] font-medium text-gray-500">
              {t('paymentMethod.label')}
            </span>
            <UiSelect
              label={t('paymentMethod.label')}
              hideLabel
              size="sm"
              value={method}
              onChange={(v) => onPatch({ paymentMethod: v as PaymentMethod })}
              options={(['BANK_TRANSFER', 'CARD', 'CASH'] as const).map((m) => ({
                value: m,
                label: t(`paymentMethod.${m}`),
              }))}
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        {/* Quick-add (inline create + auto-link) — left side. */}
        <div className="flex flex-wrap gap-2">
          {tab !== 'ignored' && isCredit && !line.matchedClientId && (
            <button
              type="button"
              onClick={onAddClient}
              className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
            >
              <UserPlus size={13} /> Adaugă client
            </button>
          )}
          {tab !== 'ignored' && !isCredit && !line.matchedSupplierId && (
            <>
              <button
                type="button"
                onClick={() => onAddSupplier(false)}
                className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
              >
                <UserPlus size={13} /> Adaugă furnizor
              </button>
              <button
                type="button"
                onClick={() => onAddSupplier(true)}
                className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
              >
                <UserPlus size={13} /> Adaugă furnizor recurent
              </button>
            </>
          )}
        </div>

        {/* Status actions — right side. */}
        <div className="flex gap-2">
          {isCashRegister && (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
            >
              <Trash2 size={13} /> Șterge
            </button>
          )}
          {tab === 'ignored' ? (
            <button
              type="button"
              onClick={onRestore}
              className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Restaurează
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onIgnore}
                className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
              >
                Ignoră
              </button>
              {tab === 'attention' && (
                <button
                  type="button"
                  onClick={onConfirm}
                  className="rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700"
                >
                  Confirmă
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showRaw && <RawDataModal txn={txn} onClose={() => setShowRaw(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Raw transaction-data popover (CLE-165). The main row shows only the relevant
// fields (title, amount, date, type); this modal exposes EVERY raw field from
// the statement line for debugging / verification.
// ---------------------------------------------------------------------------

interface RawDataModalProps {
  txn: StatementTransaction;
  onClose: () => void;
}

function RawDataModal({ txn, onClose }: RawDataModalProps) {
  // Every raw field, in a stable order. Money/dates are shown formatted next to
  // the raw value so the popover is both human-readable and a faithful dump.
  const rows: Array<{ label: string; value: string | null }> = [
    { label: 'Titlu', value: txn.title },
    { label: 'Contraparte', value: txn.counterparty },
    { label: 'Descriere completă', value: txn.description },
    { label: 'Dată', value: txn.date },
    { label: 'Dată procesare', value: txn.processingDate },
    { label: 'Tip', value: txn.type === 'credit' ? 'Credit (venit)' : 'Debit (cheltuială)' },
    { label: 'Sumă', value: `${formatCurrency(txn.amount, txn.currency)} (${txn.amount})` },
    {
      label: 'Sumă originală',
      value:
        txn.originalAmount != null
          ? `${formatCurrency(txn.originalAmount, txn.originalCurrency ?? 'EUR')} (${txn.originalAmount})`
          : null,
    },
    { label: 'Monedă originală', value: txn.originalCurrency },
    { label: 'Curs valutar', value: txn.exchangeRate != null ? String(txn.exchangeRate) : null },
    {
      label: 'Sold după',
      value: txn.balance != null ? formatCurrency(txn.balance, txn.currency) : null,
    },
    { label: 'Referință tranzacție', value: txn.bankReference },
    { label: 'Referință factură', value: txn.invoiceReference },
    { label: 'IBAN contraparte', value: txn.counterpartyIban },
    { label: 'Bancă contraparte (SWIFT/BIC)', value: txn.counterpartyBank },
    { label: 'CUI', value: txn.counterpartyCui },
    { label: 'Motiv potrivire', value: txn.matchReason },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Datele brute ale tranzacției"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Datele brute ale tranzacției</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Închide"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={16} />
          </button>
        </div>
        <dl className="divide-y divide-gray-100">
          {rows.map((r) => (
            <div key={r.label} className="grid grid-cols-3 gap-2 px-4 py-2">
              <dt className="text-xs font-medium text-gray-500">{r.label}</dt>
              <dd className="col-span-2 break-words text-xs text-gray-800">
                {r.value && r.value.trim() !== '' ? (
                  <span className="whitespace-pre-wrap">{r.value}</span>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Duplicate review banner (CLE-152). Collapsible list of skipped / flagged
// duplicate transactions so the user can verify what the importer set aside.
// ---------------------------------------------------------------------------

interface DuplicateBannerProps {
  lines: StatementTransaction[];
  skippedCount: number;
  possibleCount: number;
}

function DuplicateBanner({ lines, skippedCount, possibleCount }: DuplicateBannerProps) {
  const [expanded, setExpanded] = useState(false);

  const headline =
    skippedCount > 0
      ? `${skippedCount} ${skippedCount === 1 ? 'tranzacție duplicat detectată' : 'tranzacții duplicat detectate'} și omise`
      : `${possibleCount} ${possibleCount === 1 ? 'posibilă tranzacție duplicat marcată' : 'posibile tranzacții duplicat marcate'}`;

  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
          <CopyCheck size={16} className="text-amber-600" />
          {headline}
          {skippedCount > 0 && possibleCount > 0 && (
            <span className="text-xs font-normal text-amber-700">
              · {possibleCount} posibile marcate pentru verificare
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-xs font-medium text-amber-800 underline hover:text-amber-900"
        >
          {expanded ? 'Ascunde' : 'Arată'} detaliile
        </button>
      </div>

      {expanded && (
        <div className="mt-2 overflow-x-auto rounded border border-amber-200 bg-white">
          <table className="min-w-full divide-y divide-amber-100 text-xs">
            <thead className="bg-amber-100/50 text-amber-800">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">Dată</th>
                <th className="px-2 py-1.5 text-left font-medium">Contraparte</th>
                <th className="px-2 py-1.5 text-right font-medium">Sumă</th>
                <th className="px-2 py-1.5 text-left font-medium">Motiv</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-50 text-gray-700">
              {lines.map((t) => (
                <tr key={t.id}>
                  <td className="whitespace-nowrap px-2 py-1.5">{formatDate(t.date)}</td>
                  <td className="px-2 py-1.5">{t.counterparty ?? t.description ?? '—'}</td>
                  <td
                    className={`whitespace-nowrap px-2 py-1.5 text-right font-medium ${
                      t.type === 'credit' ? 'text-emerald-600' : 'text-red-600'
                    }`}
                  >
                    {t.type === 'credit' ? '+' : '−'}
                    {formatCurrency(t.amount, t.currency)}
                  </td>
                  <td className="px-2 py-1.5 text-gray-500">
                    {t.duplicateVerdict === 'possible'
                      ? 'Posibil duplicat — verifică'
                      : (t.matchReason ?? 'Deja importat')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface SelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  onCreate?: (name: string) => Promise<string>;
}

// Thin wrapper over the shared Select keeping this page's compact
// label-above-field layout and the "—" empty choice (clearable).
function Select({ label, value, onChange, options, onCreate }: SelectProps) {
  return (
    <div className="block">
      <span className="mb-1 block text-[11px] font-medium text-gray-500">{label}</span>
      <UiSelect
        label={label}
        hideLabel
        size="sm"
        clearable
        searchable
        value={value}
        onChange={onChange}
        placeholder="—"
        options={options}
        onCreate={onCreate}
      />
    </div>
  );
}
